import React from 'react';
import { trackError } from '@utils/datadog';

interface RenderErrorBoundaryProps {
  /** Rendered in place of the children once a render has thrown. */
  fallback: React.ReactNode;
  /** Prefixes the console line and tags the report, e.g. `ModelViewer`. */
  label: string;
  /** Reported alongside the error so a crash can be traced to a file. */
  fileName?: string;
  /** Lets the caller degrade further, e.g. swap the tile for its glyph. */
  onError?: (error: Error) => void;
  children: React.ReactNode;
}

interface RenderErrorBoundaryState {
  hasError: boolean;
}

/**
 * Contains a render-time crash inside a third-party viewer.
 *
 * <p>three.js, its viewcube gizmo and pdf.js all throw synchronously on exotic
 * geometry, a hostile GPU driver or a browser missing an API they use without
 * feature-detecting it. Without a boundary such a throw unmounts the whole
 * React tree — the user loses the entire chat because one thumbnail could not
 * render. Failures here degrade to the caller's placeholder instead.</p>
 *
 * <p>Only render-phase and lifecycle errors reach this class, and nothing else
 * catches those: react-pdf's `onLoadError` and `onSourceError`, like the 3D
 * viewer's retry overlay, are promise callbacks that a throw never reaches.</p>
 */
class RenderErrorBoundary extends React.Component<
  RenderErrorBoundaryProps,
  RenderErrorBoundaryState
> {
  state: RenderErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RenderErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(`[${this.props.label}] Render crash contained:`, error);
    trackError(error, {
      label: this.props.label,
      fileName: this.props.fileName,
      stage: 'render',
      componentStack: errorInfo.componentStack,
    });
    this.props.onError?.(error);
  }

  render(): React.ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

export default RenderErrorBoundary;
